/**
 * @fileOverview Human-looking pointer movement for the per-view walkthrough.
 *
 * Ported from `marketing/lib/pointer.mjs`, which does the same job for the recorded product videos,
 * and for the same reason: a pointer that teleports, or slides in a straight line at constant speed,
 * reads as a machine. Real pointer motion curves, accelerates away, decelerates into the target and
 * overshoots slightly before settling.
 *
 * So a move is a cubic Bézier with a perpendicular bow, sampled on an ease-in-out velocity curve,
 * with a small overshoot on longer travels and sub-pixel tremor throughout. Duration scales with
 * distance the way a hand does.
 *
 * All of it is **deterministic** — the "randomness" is hashed from the coordinates, so the same move
 * always looks the same rather than wiggling differently on every replay.
 *
 * Coordinates are the frame's own, exactly like the anchor rects; `LewViewHelp` scales them.
 */

export interface Point {
  x: number
  y: number
}

export interface Flight {
  /** Sampled positions, in order. The last one is the destination. */
  points: Point[]
  /** How long to hold each point, in milliseconds. */
  frameMs: number
  duration: number
}

/** Below this there is nothing worth animating. */
const MIN_TRAVEL = 2

/** Deterministic pseudo-random in [0,1) from a numeric seed. */
function noise(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453
  return x - Math.floor(x)
}

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2)

const cubicAt = (t: number, p0: number, p1: number, p2: number, p3: number) => {
  const u = 1 - t
  return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3
}

/**
 * Samples a bowed path from `from` to `to`.
 *
 * The control points sit along the straight line but pushed sideways, so the pointer arcs instead of
 * ruling a line. The bow grows with distance but is capped — a long sweep across the page curves
 * noticeably, a nudge to the next button barely does.
 */
function path(from: Point, to: Point, steps: number): Point[] {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const distance = Math.hypot(dx, dy) || 1

  // Perpendicular unit vector, and a bow that leans one way or the other depending on the endpoints.
  const nx = -dy / distance
  const ny = dx / distance
  const seed = from.x + from.y * 3 + to.x * 7 + to.y * 11
  const bow = Math.min(distance * 0.16, 46) * (noise(seed) < 0.5 ? -1 : 1)

  const c1 = { x: from.x + dx * 0.3 + nx * bow, y: from.y + dy * 0.3 + ny * bow }
  const c2 = { x: from.x + dx * 0.7 + nx * bow * 0.6, y: from.y + dy * 0.7 + ny * bow * 0.6 }

  const points: Point[] = []
  for (let i = 1; i <= steps; i++) {
    const t = easeInOutCubic(i / steps)
    // Sub-pixel tremor, strongest mid-flight and settling to nothing on arrival.
    const tremor = (1 - Math.abs(t - 0.5) * 2) * 0.9
    points.push({
      x: cubicAt(t, from.x, c1.x, c2.x, to.x) + (noise(seed + i) - 0.5) * tremor,
      y: cubicAt(t, from.y, c1.y, c2.y, to.y) + (noise(seed + i * 1.7) - 0.5) * tremor,
    })
  }
  return points
}

/**
 * The path from `from` to `to`, or null when there is nothing to animate.
 *
 * Fitts-like: longer travel takes longer, but sub-linearly. Longer moves also overshoot a little and
 * pull back, the way a hand does — the pull-back is the tail of the same sampled path, because the
 * curve is aimed just past the target rather than at it.
 */
export function flightTo(from: Point | null, to: Point): Flight | null {
  if (!from) return { points: [to], frameMs: 0, duration: 0 }

  const distance = Math.hypot(to.x - from.x, to.y - from.y)
  if (distance < MIN_TRAVEL) return null

  const duration = Math.min(900, Math.max(240, 200 + distance * 0.6))
  const steps = Math.max(6, Math.round(duration / 16))

  const seed = to.x + to.y * 3
  const overshoot = distance > 220 ? 4 + noise(seed) * 7 : 0
  const aim = overshoot
    ? {
        x: to.x + ((to.x - from.x) / distance) * overshoot,
        y: to.y + ((to.y - from.y) / distance) * overshoot,
      }
    : to

  const points = path(from, aim, steps)
  // Land exactly on the target, so a step's pointer position is not an overshoot plus tremor.
  points.push(to)

  return { points, frameMs: duration / steps, duration }
}

/**
 * Where the pointer should aim for a region.
 *
 * Not the centre: a region is often a whole card or table, and a pointer parked in the middle of one
 * covers its content. Just inside the top-left corner reads as "this thing here" and leaves the rest
 * of it visible — the same corner the badge marks.
 */
export function aimFor(rect: { top: number; left: number; width: number; height: number }): Point {
  return {
    x: rect.left + Math.min(rect.width * 0.5, 42),
    y: rect.top + Math.min(rect.height * 0.5, 24),
  }
}

/** …but for something small and clickable, the middle is exactly right. */
export function aimForControl(rect: { top: number; left: number; width: number; height: number }): Point {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
}
